<template>
  <div class="page page-notifications content">
    <h1 class="page__title">{{ t('notifications.title') }}</h1>
    <div class="notifications__actions">
      <div
        for="toggle-unread-notifications"
        class="notifications__filter"
      >
        <Toggle id="notifications__show-already-seen" v-model="showAlreadySeen" :height="25" />
        <label
          for="notifications__show-already-seen"
          @click="showAlreadySeen = !showAlreadySeen"
        >
          {{ t('notifications.toggleUnread') }}
        </label>
      </div>
      <button
        v-if="hasUnreadNotifications"
        class="button button--secondary"
        @click="markAllAsRead"
      >
        {{ t('notifications.markAllAsRead') }}
      </button>
    </div>
    <ul class="notifications__list">
      <li
        v-for="notification in notifications"
        :key="notification.id"
        class="notifications__list-item"
      >
        <Notification :notification="notification" />
      </li>
    </ul>
    <button
      class="button button--secondary notifications__more"
      @click="showMoreSteps()"
      v-if="unlimitedNotifications.length > maxNotifications"
    >
      {{ t('notifications.showMore') }}
    </button>
  </div>
</template>

<script setup>
  import Notification from '@/components/Notification.vue'
  import { useStrings } from '@/composables/useStrings.ts'
  import { useCubeStore } from '@/stores/useCubeStore'
  import { storeToRefs } from 'pinia'
  import Toggle from '@vueform/toggle'
  import { computed, ref } from 'vue'

  const cubeStore = useCubeStore()
  const { notifications: rawNotifications } = storeToRefs(cubeStore)
  const { t } = useStrings()

  const showAlreadySeen = ref(false)
  const maxNotifications = ref(10)
  const showMoreSteps = () => {
    maxNotifications.value += 10
  }

  const unreadNotifications = computed(() => {
    return rawNotifications.value.filter((notification) => notification.unread === 1)
  })

  const hasUnreadNotifications = computed(() => unreadNotifications.value.length > 0)

  const unlimitedNotifications = computed(() => {
    if (showAlreadySeen.value) {
      return rawNotifications.value
    }
    else {
      return unreadNotifications.value
    }
  })

  const notifications = computed(() => {
    return unlimitedNotifications.value.slice(0, maxNotifications.value)
  })

  const markAllAsRead = () => {
    cubeStore.markNotificationsAsRead({ all: true })
  }
</script>

<style lang="scss" scoped>
  .page-notifications {
    max-width: $content-page-width;
  }

  .notifications {
    &__headline {
      margin: $spacer40 0 $spacer10;
      font-size: $font-size-lg;
      font-weight: bold;
    }

    &__actions {
      display: flex;
      margin-bottom: $spacer30;
      flex-wrap: wrap;
      justify-content: space-between;

      .button {

        @media (max-width: $bp-ssm - 1px) {
          flex-basis: 100%;
        }
      }
    }

    &__filters {
      padding: 0 $spacer30;
      margin-bottom: $spacer05;
    }

    &__filter {
      display: inline-flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: $spacer075;

      @media (max-width: $bp-ssm - 1px) {
        flex-basis: 100%;
      }

      label {
        margin-right: $spacer075;
        margin-left: $spacer10;
      }
    }

    &__list {
      margin: $spacer10 0;
      padding: 0;
      list-style: none;

      &-item {
        transition: $transition-base;
        $offset: $spacer10;
        margin-left: $offset * -1;
        margin-right: $offset * -1;
        padding-left: $offset;
        padding-right: $offset;
        padding-bottom: $spacer05;
        padding-top: $spacer05;
        border-bottom: 1px solid $grey-light;

        &:last-child {
          border-bottom: none;
        }

        &:hover,
        &:active,
        &:focus {
          background: $grey-lightest;
        }
      }
    }

    &__more {
      display: block;
      margin: $spacer025 auto $spacer20;
      max-width: 300px;
    }
  }
</style>
